import * as React from 'react';
import { IProps } from '../common/props';
import { IFileInputCommonProps } from './FileInput';
import * as Classes from '../common/classes';
import classNames from 'classnames';
import { Button } from '../button/Button';
import { useDropzone } from 'react-dropzone';
import { useRef, useState } from 'react';
// @ts-ignore
import { v4 as uuidv4 } from 'uuid';
import { FileInputFile } from './FileInputFile';
import {
  CancelUpload,
  defaultMaxFileSize,
  FileInputValue,
  IFileInputValue,
  UloadingInfo,
  UPLOAD_PROGRESS_INFINITE,
} from './props';
import { InputGroup } from '../input-group/InputGroup';
import { useAppConfig } from '@mc/react-app-base';
import { filesize } from 'filesize';
import { showInfo } from '../modal/modalFunctions';

export type MultiFileInputValue<FILE_VALUE> = FILE_VALUE[];

export interface IMultiFileInputProps<FILE_VALUE extends FileInputValue>
  extends IFileInputCommonProps<FILE_VALUE>,
    IProps {
  value?: FILE_VALUE[];
  onChange: (value?: FILE_VALUE[]) => void;
}

export type MultiFileInputProps<FILE_VALUE extends FileInputValue> =
  IMultiFileInputProps<FILE_VALUE>;

type UploadingInfoMap = { [key: string]: UloadingInfo };
type UploadingActiveMap = { [key: string]: boolean };
type UploadingProgressMap = { [key: string]: number };
type CancelUploadMap = { [key: string]: CancelUpload };

/**
 * Komponenta pro editaci jednoho souboru s možností změny a smazání.
 */
export const MultiFileInput: <FILE_VALUE extends FileInputValue>(
  props: MultiFileInputProps<FILE_VALUE>
) => React.ReactElement<MultiFileInputProps<FILE_VALUE>> = ({
  className,
  onChange,
  placeholder,
  disabled,
  getHref,
  onUpload,
  onUploadStart,
  onUploadStop,
  onDownload,
  onAfterDelete,
  accept,
  value,
}) => {
  const uploadingActive = useRef<UploadingActiveMap>({});
  const uploadingCount = useRef<number>(0);
  const [uploadingInfo, setUploadingInfo] = useState<UploadingInfoMap>({});
  const currValue = useRef<FileInputValue[]>();
  const currUploadingProgress = useRef<UploadingProgressMap>({});
  const [uploadingProgress, setUploadingProgress] =
    useState<UploadingProgressMap>({});
  const cancelUpload = useRef<CancelUploadMap>({});

  const realValue = Array.isArray(value) ? value : undefined;

  currUploadingProgress.current = uploadingProgress;
  currValue.current = realValue;

  const handleOnDrop = async (files: File[]) => {
    if (uploadingCount.current === 0) {
      onUploadStart && onUploadStart();
    }

    // Necháme jen soubrory, které jsou menší než maxFileSize
    const appConfig = useAppConfig();
    const maxFileSize = appConfig.maxFileSize || defaultMaxFileSize;
    const warningMessages = [];
    const filteredFiles: File[] = [];
    for (const file of files) {
      if (file.size > maxFileSize) {
        warningMessages.push(
          `Soubor ${file.name} je příliš velký (${filesize(file.size, {
            locale: 'cs',
          })}).`
        );
      } else {
        filteredFiles.push(file);
      }
    }
    if (warningMessages.length > 0) {
      showInfo({
        intent: 'warning',
        title: 'Některé soubory nebyly nahrány',
        message: (
          <div>
            {`Maximální povolená velikost souboru je ${filesize(maxFileSize, {
              locale: 'cs',
            })}.`}
            <br />
            <br />
            {warningMessages.map((message) => (
              <div>{message}</div>
            ))}
          </div>
        ),
      });
    }
    // -----------------------

    const newUloadingInfo = { ...uploadingInfo };
    const newUploadingProgress = { ...currUploadingProgress.current };
    const newItems: IFileInputValue[] = [];
    filteredFiles.forEach((file) => {
      const id = uuidv4();
      const item: FileInputValue = {
        id,
        type: file.type,
        name: file.name,
      };

      newUloadingInfo[item.id] = {
        fileName: file.name,
        type: file.type,
        size: file.size,
      };

      newUploadingProgress[item.id] = UPLOAD_PROGRESS_INFINITE;

      uploadingActive.current[item.id] = true;

      newItems.push(item);
    });

    // ---

    setUploadingInfo(newUloadingInfo);
    setUploadingProgress(newUploadingProgress);

    // ---
    const currItems = currValue.current || [];
    // @ts-ignore
    onChange([...currItems, ...newItems]);

    for (let a = 0; a < filteredFiles.length; a++) {
      uploadingCount.current++;

      const file = filteredFiles[a];
      const item = newItems[a];
      onUpload(
        file,
        (progress) => {
          if (uploadingActive.current[item.id]) {
            let newUploadingProgress = {
              ...currUploadingProgress.current,
              [item.id]: progress.loaded / progress.total,
            };

            setUploadingProgress(newUploadingProgress);
            currUploadingProgress.current = newUploadingProgress;
          }
        },
        (cancel: CancelUpload) => {
          cancelUpload.current[item.id] = cancel;
        }
      ).then((newValue) => {
        if (uploadingActive.current[item.id]) {
          uploadingActive.current[item.id] = false;

          const n = currValue.current
            ? currValue.current.findIndex((v) => v.id === item.id)
            : -1;

          if (n >= 0) {
            let newItems = currValue.current || [];
            newItems = [...newItems];
            newItems[n] = newValue;

            // @ts-ignore
            onChange(newItems);
          }

          uploadingCount.current--;
          if (uploadingCount.current === 0) {
            onUploadStop && onUploadStop();
          }
        }
      });
    }
  };

  const handleDelete = (id: string) => {
    const n = realValue ? realValue.findIndex((v) => v.id === id) : -1;
    if (n >= 0) {
      let newItems = realValue || [];
      newItems = [...newItems.slice(0, n), ...newItems.slice(n + 1)];
      // @ts-ignore
      onChange(newItems);
      onAfterDelete?.();
    }

    if (uploadingActive.current[id]) {
      uploadingActive.current[id] = false;

      uploadingCount.current--;
      if (uploadingCount.current === 0) {
        onUploadStop && onUploadStop();
      }
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: accept,
    multiple: true,
    preventDropOnDocument: true,
    onDrop: handleOnDrop,
  });

  const rootProps: any = getRootProps({
    className: classNames(className, Classes.FILE_INPUT),
  });

  const { onClick, ...parentRootProps } = rootProps;
  return (
    <div className={Classes.MULTI_FILE_INPUT}>
      <div {...parentRootProps}>
        <input {...getInputProps()} />
        {isDragActive ? (
          <div className={Classes.FILE_INPUT_DRAGGING}>
            Sem přetáhněte soubor
          </div>
        ) : (
          <>
            <InputGroup
              placeholder={placeholder || 'Vyberte nebo přetáhněte soubor'}
              readOnly={!disabled}
              disabled={disabled}
              className={classNames(
                className,
                Classes.INPUT_READONLY_STANDARD,
                Classes.FILE_INPUT_INPUT
              )}
            />
            <Button
              disableStopPropagation
              disabled={disabled}
              className={Classes.FILE_INPUT_ACTION}
              onClick={onClick}
            >
              Vybrat
            </Button>
          </>
        )}
      </div>
      <div className={Classes.MULTI_FILE_INPUT_FILES}>
        {realValue?.map((item, index) => (
          <FileInputFile<any>
            key={index}
            disabled={disabled}
            getHref={getHref}
            onDownload={onDownload}
            onDelete={() => handleDelete(item.id)}
            uploadingInfo={
              uploadingActive.current[item.id]
                ? uploadingInfo[item.id]
                : undefined
            }
            value={item}
            uploadingProgress={
              uploadingActive.current[item.id] ? uploadingProgress[item.id] : 0
            }
          />
        ))}
      </div>
    </div>
  );
};
